import * as THREE from "three";
import { fireProjectile } from "./weapons/Projectile";
import { fireProjectileBolt } from "./weapons/LightningProjectile";
import { fireProjectileLaser } from "./weapons/LaserProjectile";
import {
  checkTankCollisions, // Import of centralised function
} from "./environment";
import { TANK_CONFIG, ENVIRONMENT_CONFIG, RESPAWN_ZONE_CONFIG, MOUSE_CONFIG } from "./config";
import { TankMovementController } from "./controllers/TankMovementController";
import { Environment } from "./environment";
import { BarrelController } from "./controllers/BarrelController";
import { TankSpawning } from "./TankSpawning";
import { TankModelConfig, TANK_MODELS } from "./tanks/TankModel";
import { TrainingTrackManager } from "./training/ModularTrainingTrackManager";
import { TrainingTrackService } from "./training/TrainingTrackService";
import { BotManager } from "./BotManager";
import { Bot } from "./Bot";
import { EventManager } from "./EventManager";
import { MobileController } from "./controllers/MobileController";

// [NEW] Cache for geometries and materials
const tankGeometryCache = new Map<string, Map<string, THREE.BufferGeometry>>();
const tankMaterialCache = new Map<string, Map<string, THREE.Material>>();

// [NEW] Helper to get cached geometry
function getCachedGeometry(modelId: string, key: string, factory: () => THREE.BufferGeometry): THREE.BufferGeometry {
  if (!tankGeometryCache.has(modelId)) {
    tankGeometryCache.set(modelId, new Map());
  }
  const modelCache = tankGeometryCache.get(modelId)!;
  if (!modelCache.has(key)) {
    const geometry = factory();
    geometry.userData.isShared = true; // Mark as shared
    modelCache.set(key, geometry);
  }
  return modelCache.get(key)!;
}

// [NEW] Helper to get cached material
function getCachedMaterial(modelId: string, key: string, factory: () => THREE.Material): THREE.Material {
  if (!tankMaterialCache.has(modelId)) {
    tankMaterialCache.set(modelId, new Map());
  }
  const modelCache = tankMaterialCache.get(modelId)!;
  if (!modelCache.has(key)) {
    const material = factory();
    material.userData.isShared = true; // Mark as shared
    modelCache.set(key, material);
  }
  return modelCache.get(key)!;
}

// Declare global window interface
declare global {
  interface Window {
    socket?: any;
    playerTank?: Tank;
    gameState?: {
      tanks: Tank[];
      scene?: THREE.Scene;
      environmentService?: {
        getRandomRespawnPoint: () => {
          position: THREE.Vector3;
          rotation: number;
        } | null;
      };
      trainingTrackManager?: TrainingTrackManager;
      trainingTrackService?: TrainingTrackService;
      Bot?: typeof Bot;
      botManager?: BotManager;
    };
    tanks?: Tank[];
    remoteTanks?: Tank[];
    showDamageIndicator?: (damage: number) => void;
  }
}

export class Tank {
  mesh: THREE.Object3D;
  body: THREE.Mesh;
  //body: THREE.Group;
  turret: THREE.Object3D | null = null;
  barrel: THREE.Object3D;
  barrelPivot: THREE.Group; // New property for barrel pivot
  wheels: THREE.Mesh[];
  muzzleFlash: THREE.PointLight | null = null;
  playerId: string; // Unique Tank Identifier (UUID)
  modelId: string; // Tank model ID
  username: string = "Anonymous"; // Username for display
  usernameLabel: THREE.Sprite | null = null; // Label to display username

  movementSystem: TankMovementController; // Correct type
  mobileController: MobileController | null = null; // Mobile controller

  // Movement parameters
  maxSpeed = TANK_CONFIG.MOVEMENT.MAX_SPEED;
  maxSpeedKMH = TANK_CONFIG.MOVEMENT.MAX_SPEED_KMH;
  speedFactor = TANK_CONFIG.MOVEMENT.SPEED_FACTOR;
  acceleration = TANK_CONFIG.MOVEMENT.ACCELERATION;
  deceleration = TANK_CONFIG.MOVEMENT.DECELERATION;
  turnSpeed = TANK_CONFIG.MOVEMENT.TURN_SPEED;
  maxTurnSpeed = 0.04; // Reduced maximum turn speed
  currentSpeed = 0;
  currentTurnSpeed = 0;

  // Health parameters
  maxHealth: number = TANK_CONFIG.HEALTH.MAX_HEALTH;
  health: number = TANK_CONFIG.HEALTH.MAX_HEALTH;
  armor: number = TANK_CONFIG.HEALTH.ARMOR;
  damageResistance: number = TANK_CONFIG.HEALTH.DAMAGE_RESISTANCE;
  isDestroyed: boolean = false;
  damageSmoke: boolean = false;
  lastDamageTime: number = 0;
  invulnerableUntil: number = 0;
  respawnTime: number = RESPAWN_ZONE_CONFIG.RESPAWN_TIME;

  // Shooting parameters
  shotCooldown = TANK_CONFIG.SHOOTING.COOLDOWN;
  lastShotTime = 0;
  projectileType = "standard";

  // New control system
  controls = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    handbrake: false,
    barrelUp: false,
    barrelDown: false,
  };

  // Variables to track previous control states
  private previousControls = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    handbrake: false,
    barrelUp: false,
    barrelDown: false,
  };

  // Variables to track previous speed
  private previousSpeed = 0;

  // References to environment elements
  environment: Environment | null = null;

  // Flag for boosting
  isBoosting = false;

  // Turret parameters
  targetTurretAngle = 0;
  turretRotationSpeed = TANK_CONFIG.TURRET.ROTATION_SPEED;
  turretRotationVelocity = 0;
  turretTurnDirection = 0;

  // Barrel parameters
  private barrelController: BarrelController;
  private spawnController: TankSpawning;
  private spawning: TankSpawning;

  // Variables to track turret angle changes
  private lastTurretAngle: number = 0;
  private lastTurretLogTime: number = 0;
  private readonly TURRET_LOG_THROTTLE_MS: number = 5000; // Log every 5000ms

  private lastSentPosition: THREE.Vector3 | null = null;
  private lastSentRotation: number | null = null;
  private lastSentTurretRotation: number | null = null;
  private readonly POSITION_UPDATE_THRESHOLD = 0.01; // Minimum position change to be sent
  private readonly ROTATION_UPDATE_THRESHOLD = 0.01; // Minimum position change to be sent
  private lastNetworkUpdateTime: number = 0;
  private readonly NETWORK_UPDATE_INTERVAL = ENVIRONMENT_CONFIG.UPDATE_THROTTLE; // Throttle logs 100ms

  private initialized: boolean = false;

  public previousPosition: THREE.Vector3;

  constructor(scene: THREE.Scene, playerId?: string, modelId: string = "standard") {
    this.mesh = new THREE.Group();
    this.mesh.userData.type = "tank";
    this.mesh.userData.isTank = true;
    this.mesh.userData.tank = this; // Store reference to Tank instance

    // Set playerId from parameter or generate new UUID
    this.playerId = playerId || this.mesh.uuid;

    // Save model ID
    this.modelId = modelId;

    // Initialize spawning controller
    this.spawning = new TankSpawning(this);
    this.spawnController = this.spawning;

    // Default tank is not initialized
    this.initialized = false;

    // Get tank model configuration
    const tankModel = TANK_MODELS[this.modelId] || TANK_MODELS.standard;
    console.log(`[Tank] Initializing tank with model: ${tankModel.name} (${this.modelId})`);

    // Tank body - adjust to selected model
    // [CHANGED] Use cached geometry
    const bodyGeometry = getCachedGeometry(
      this.modelId,
      "body",
      () =>
        new THREE.BoxGeometry(
          tankModel.dimensions.body.width,
          tankModel.dimensions.body.height,
          tankModel.dimensions.body.length,
        ),
    );
    const bodyMaterialBase = getCachedMaterial(
      this.modelId,
      "body",
      () =>
        new THREE.MeshStandardMaterial({
          color: tankModel.colors.body,
          roughness: 0.7, // Matt
          metalness: 0.3, // soft metalic shine
        }),
    );
    const bodyMaterial = bodyMaterialBase.clone();
    bodyMaterial.userData.isShared = false;
    this.body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    this.body.position.y = 0.6;
    this.body.castShadow = true;
    this.body.receiveShadow = true;
    this.mesh.add(this.body);

    // Tank tracks - adjust to selected model
    // [CHANGED] Use cached geometry
    const trackGeometry = getCachedGeometry(
      this.modelId,
      "tracks",
      () =>
        new THREE.BoxGeometry(
          tankModel.dimensions.tracks.width,
          tankModel.dimensions.tracks.height,
          tankModel.dimensions.tracks.length,
        ),
    );
    const trackMaterialBase = getCachedMaterial(
      this.modelId,
      "tracks",
      () =>
        new THREE.MeshStandardMaterial({
          color: tankModel.colors.tracks,
        }),
    );
    const trackMaterial = trackMaterialBase.clone();
    trackMaterial.userData.isShared = false;

    const leftTrack = new THREE.Mesh(trackGeometry, trackMaterial);
    leftTrack.position.set(-1.2, 0.2, 0);
    leftTrack.castShadow = true;
    leftTrack.receiveShadow = true;
    this.mesh.add(leftTrack);

    const rightTrack = new THREE.Mesh(trackGeometry, trackMaterial);
    rightTrack.position.set(1.2, 0.2, 0);
    rightTrack.castShadow = true;
    rightTrack.receiveShadow = true;
    this.mesh.add(rightTrack);

    // Tank turret - adjust to selected model
    // [CHANGED] Use cached geometry
    const turretGeometry = getCachedGeometry(
      this.modelId,
      "turret",
      () =>
        new THREE.CylinderGeometry(
          tankModel.dimensions.turret.radius,
          tankModel.dimensions.turret.radius + 0.2,
          tankModel.dimensions.turret.height,
          16,
        ),
    );
    const turretMaterialBase = getCachedMaterial(
      this.modelId,
      "turret",
      () =>
        new THREE.MeshStandardMaterial({
          color: tankModel.colors.turret,
        }),
    );
    const turretMaterial = turretMaterialBase.clone();
    turretMaterial.userData.isShared = false;
    this.turret = new THREE.Mesh(turretGeometry, turretMaterial);
    this.turret.position.y = 1.1;
    this.turret.rotation.y = 0;
    this.turret.castShadow = true;
    this.turret.receiveShadow = true;
    this.mesh.add(this.turret);

    // Tank antenna - adjust to selected model
    const antennaGeometry = getCachedGeometry(
      this.modelId,
      "antenna",
      () => new THREE.CylinderGeometry(0.02, 0.02, 1, 8),
    );
    const antenna = new THREE.Mesh(antennaGeometry, turretMaterial);
    antenna.position.set(0.5, 0.75, 0); // On turret
    this.turret.add(antenna);

    // Tank barrel - adjust to selected model
    // [CHANGED] Use cached geometry
    const barrelGeometry = getCachedGeometry(
      this.modelId,
      "barrel",
      () =>
        new THREE.CylinderGeometry(
          tankModel.dimensions.barrel.radius,
          tankModel.dimensions.barrel.radius,
          tankModel.dimensions.barrel.length,
          8,
        ),
    );
    const barrelMaterialBase = getCachedMaterial(
      this.modelId,
      "barrel",
      () =>
        new THREE.MeshStandardMaterial({
          color: tankModel.colors.barrel,
        }),
    );
    const barrelMaterial = barrelMaterialBase.clone();
    barrelMaterial.userData.isShared = false;

    // Create a container for the barrel to handle elevation properly
    this.barrelPivot = new THREE.Group();
    this.barrelPivot.position.set(0, 0, 0); // Change: Reset pivot position

    // Add barrel to pivot with correct orientation
    this.barrel = new THREE.Mesh(barrelGeometry, barrelMaterial);
    this.barrel.position.set(0, 0, tankModel.dimensions.barrel.length / 2); // Change: Move barrel forward in pivot
    this.barrel.rotation.x = -Math.PI / 2; // Keep this to point forward
    this.barrel.castShadow = true;
    this.barrelPivot.add(this.barrel);

    // Add pivot to turret
    this.turret.add(this.barrelPivot);

    // Tank muzzle flash - adjust to selected model
    // Perf: one PointLight per tank multiplies forward-render cost for the WHOLE
    // scene (every shader iterates all lights). Keep it invisible except the
    // 100ms flash - invisible lights are skipped by the renderer, intensity 0 is not.
    this.muzzleFlash = new THREE.PointLight(0xff9933, 0, 5);
    this.muzzleFlash.visible = false;
    this.muzzleFlash.position.set(0, 0, 1);
    this.barrel.add(this.muzzleFlash);

    // Tank wheel
    // Reduced segments for performance (12 instead of 16)
    // [CHANGED] Use cached geometry
    const wheelGeometry = getCachedGeometry(this.modelId, "wheel", () => {
      const geo = new THREE.CylinderGeometry(0.2, 0.2, 0.4, 12);
      geo.rotateZ(Math.PI / 2);
      return geo;
    });
    const wheelMaterialBase = getCachedMaterial(
      this.modelId,
      "wheel",
      () => new THREE.MeshStandardMaterial({ color: 0x333333 }),
    );
    const wheelMaterial = wheelMaterialBase.clone();
    wheelMaterial.userData.isShared = false;
    // wheelGeometry.rotateZ(Math.PI / 2); // [CHANGED] Rotation moved to factory

    // Add visible element to each wheel
    // [CHANGED] Use cached geometry
    const wheelDetailGeometry = getCachedGeometry(
      this.modelId,
      "wheelDetail",
      () => new THREE.BoxGeometry(0.05, 0.05, 0.3),
    );
    const detailMaterialBase = getCachedMaterial(
      this.modelId,
      "wheelDetail",
      () => new THREE.MeshStandardMaterial({ color: 0x222222 }),
    );
    const detailMaterial = detailMaterialBase.clone();
    detailMaterial.userData.isShared = false;

    const frontLeftDetail = new THREE.Mesh(wheelDetailGeometry, detailMaterial);
    frontLeftDetail.position.set(0, 0.15, 0);

    const frontRightDetail = new THREE.Mesh(wheelDetailGeometry, detailMaterial);
    frontRightDetail.position.set(0, 0.15, 0);

    const frontLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    frontLeftWheel.position.set(-1, 0.2, -1.5);
    frontLeftWheel.castShadow = true;
    frontLeftWheel.receiveShadow = true;
    this.mesh.add(frontLeftWheel);

    frontLeftWheel.add(frontLeftDetail);

    const frontRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    frontRightWheel.position.set(1, 0.2, -1.5);
    frontRightWheel.castShadow = true;
    frontRightWheel.receiveShadow = true;
    this.mesh.add(frontRightWheel);

    frontRightWheel.add(frontRightDetail);

    const backLeftWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    backLeftWheel.position.set(-1, 0.2, 1.5);
    backLeftWheel.castShadow = true;
    backLeftWheel.receiveShadow = true;
    this.mesh.add(backLeftWheel);

    const backRightWheel = new THREE.Mesh(wheelGeometry, wheelMaterial);
    backRightWheel.position.set(1, 0.2, 1.5);
    backRightWheel.castShadow = true;
    backRightWheel.receiveShadow = true;
    this.mesh.add(backRightWheel);

    this.wheels = [frontLeftWheel, frontRightWheel, backLeftWheel, backRightWheel];

    // Tank speed and parameters initialization
    this.currentSpeed = 0;
    this.turretRotationSpeed = 0;

    // Apply statistics from the tank model
    this.maxSpeed = tankModel.stats.maxSpeed;
    this.maxSpeedKMH = tankModel.stats.maxSpeedKMH;
    this.speedFactor = tankModel.stats.speedFactor;
    this.acceleration = tankModel.stats.acceleration;
    this.turnSpeed = tankModel.stats.turnSpeed;
    this.maxHealth = tankModel.stats.health;
    this.health = tankModel.stats.health;
    this.armor = tankModel.stats.armor;
    this.projectileType = tankModel.projectilesConfig.TYPE;

    // Add debug logs
    console.log(
      `[Tank] Applied model stats for ${tankModel.name} - maxSpeed: ${this.maxSpeed}, maxSpeedKMH: ${this.maxSpeedKMH}`,
    );

    // Initialize movement controller with correct parameters - moved to the end to use model parameters
    this.movementSystem = new TankMovementController(scene, this, this.wheels);

    // Ensure all tank elements are visible
    this.mesh.visible = true;
    this.mesh.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.visible = true;
      }
    });

    // Initialize controls
    this.controls = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      handbrake: false,
      barrelUp: false,
      barrelDown: false,
    };

    // Initialize barrel controller AFTER controls are set
    this.barrelController = new BarrelController(this.barrel, this.barrelPivot, {
      barrelUp: this.controls.barrelUp,
      barrelDown: this.controls.barrelDown,
    });

    // Initialize spawn controller
    this.spawnController = new TankSpawning(this);

    this.previousPosition = this.mesh.position.clone();

    // Create username label
    this.createUsernameLabel();
  }

  shoot(scene: THREE.Scene) {
    // Check if tank is destroyed
    if (this.isDestroyed) {
      //console.log(`[Tank] Cannot shoot - tank is destroyed: ${this.playerId}`);
      return;
    } else {
      //console.log(`[Tank] Tank ${this.playerId} is ready to shoot`);
    }

    // Sprawdź cooldown
    const now = Date.now();
    if (now - this.lastShotTime < this.shotCooldown) {
      return;
    }
    this.lastShotTime = now;

    // Flash effect (light visible only during flash so it costs nothing otherwise)
    if (this.muzzleFlash) {
      this.muzzleFlash.visible = true;
      this.muzzleFlash.intensity = 5;
      setTimeout(() => {
        if (this.muzzleFlash) {
          this.muzzleFlash.intensity = 0;
          this.muzzleFlash.visible = false;
        }
      }, 100);
    }

    //this.mesh.updateMatrixWorld(true);
    //this.turret.updateMatrixWorld(true);
    //this.barrelPivot.updateMatrixWorld(true);
    // Upewnij się, że macierz transformacji jest aktualna
    this.barrel.updateMatrixWorld(true);

    // Get barrel direction and position in world space
    const barrelBase = new THREE.Vector3();
    const barrelTip = new THREE.Vector3();

    // Get positions in world space
    this.barrelPivot.getWorldPosition(barrelBase);
    this.barrel.getWorldPosition(barrelTip);

    // Calculate direction from base to tip
    const direction = new THREE.Vector3().subVectors(barrelTip, barrelBase).normalize();

    // Move firing position slightly forward
    const firingPos = barrelTip.clone().add(direction.clone().multiplyScalar(0.5));

    // Create projectile and send network event
    if (this.projectileType === "laser") {
      fireProjectileLaser(scene, firingPos, direction, this.playerId);
    } else if (this.projectileType === "bolt") {
      fireProjectileBolt(scene, firingPos, direction, this.playerId);
    } else if (this.projectileType === "bolt") {
      fireProjectile(scene, firingPos, direction, this.playerId);
    } else {
      //use standard
      fireProjectile(scene, firingPos, direction, this.playerId);
    }

    // Send shot event over network if this is the local player's tank
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankShot", {
        playerId: this.playerId,
        position: firingPos.toArray(),
        direction: direction.toArray(),
        timestamp: Date.now(),
      });
    }
  }

  update(deltaTime: number) {
    // Store previous position at the start of update
    this.previousPosition.copy(this.mesh.position);

    // If tank is destroyed, do not allow movement
    if (this.isDestroyed) {
      // Ensure all movement parameters are reset
      this.currentSpeed = 0;
      this.currentTurnSpeed = 0;

      // Disable all controls
      this.controls = {
        forward: false,
        backward: false,
        left: false,
        right: false,
        barrelUp: false,
        barrelDown: false,
        handbrake: false,
      };

      // Stop movement on the server
      this.stopMovingNetwork();

      // Do not perform further updates
      return;
    }

    // Check if controls have changed
    if (
      this.controls.forward !== this.previousControls.forward ||
      this.controls.backward !== this.previousControls.backward ||
      this.controls.left !== this.previousControls.left ||
      this.controls.right !== this.previousControls.right ||
      this.controls.handbrake !== this.previousControls.handbrake ||
      this.controls.barrelUp !== this.previousControls.barrelUp ||
      this.controls.barrelDown !== this.previousControls.barrelDown
    ) {
      // Update barrel controller controls when tank controls change
      if (this.barrelController) {
        this.barrelController.setControls({
          barrelUp: this.controls.barrelUp,
          barrelDown: this.controls.barrelDown,
        });
      }

      // Update previous states
      this.previousControls.forward = this.controls.forward;
      this.previousControls.backward = this.controls.backward;
      this.previousControls.left = this.controls.left;
      this.previousControls.right = this.controls.right;
      this.previousControls.handbrake = this.controls.handbrake;
      this.previousControls.barrelUp = this.controls.barrelUp;
      this.previousControls.barrelDown = this.controls.barrelDown;
    }

    // Update turret rotation
    if (this.turret) {
      this.updateTurretRotation(deltaTime);
    }

    // Update barrel elevation
    if (this.barrelController) {
      this.barrelController.update(deltaTime);
    }

    // Update movement in FPS style
    this.updateMovement(deltaTime);
  }

  updateMovement(deltaTime: number) {
    this.movementSystem.update(deltaTime, this.isDestroyed);
  }

  updateTurretRotation(deltaTime: number) {
    if (!this.turret || this.isDestroyed) return;

    // Rotate turret based on turn direction
    if (this.turretTurnDirection !== 0) {
      this.turret.rotation.y += this.turretTurnDirection * this.turretRotationSpeed * deltaTime;

      // Log turret rotation changes (with frequency throttling)
      const currentTime = Date.now();
      if (currentTime - this.lastTurretLogTime > this.TURRET_LOG_THROTTLE_MS) {
        this.lastTurretLogTime = currentTime;
      }
    }
  }

  setTurretRotation(angle: number) {
    if (!this.turret) return;
    this.turret.rotation.y = angle;

    // Log turret rotation changes (with frequency throttling)
    const now = Date.now();
    const angleDifference = Math.abs(angle - this.lastTurretAngle);

    // Log only if angle changed significantly or enough time has passed since last log
    if (angleDifference > 0.1 || now - this.lastTurretLogTime > this.TURRET_LOG_THROTTLE_MS) {
      this.lastTurretAngle = angle;
      this.lastTurretLogTime = now;
    }
  }

  setBarrelElevation(elevation: number) {
    if (this.barrelController) {
      this.barrelController.setElevation(elevation);
    }
  }

  startMovingForward(): void {
    if (this.isDestroyed) return;
    this.controls.forward = true;
    this.controls.backward = false;
    this.movementSystem.setControl("forward", true);
  }

  stopMovingForward(): void {
    if (this.isDestroyed) return;
    this.controls.forward = false;
    this.movementSystem.setControl("forward", false);
  }

  startMovingBackward(): void {
    if (this.isDestroyed) return;
    this.controls.backward = true;
    this.controls.forward = false;
    this.movementSystem.setControl("backward", true);
  }

  stopMovingBackward(): void {
    if (this.isDestroyed) return;
    this.controls.backward = false;
    this.movementSystem.setControl("backward", false);
  }

  startTurningLeft(): void {
    if (this.isDestroyed) return;
    this.controls.left = true;
    this.movementSystem.setControl("left", true);
  }

  stopTurningLeft(): void {
    if (this.isDestroyed) return;
    this.controls.left = false;
    this.movementSystem.setControl("left", false);
  }

  startTurningRight(): void {
    if (this.isDestroyed) return;
    this.controls.right = true;
    this.movementSystem.setControl("right", true);
  }

  stopTurningRight(): void {
    if (this.isDestroyed) return;
    this.controls.right = false;
    this.movementSystem.setControl("right", false);
  }

  // Methods for network control of movement
  startMovingForwardNetwork(): void {
    if (this.isDestroyed) return;
    this.controls.forward = true;
    this.controls.backward = false;
    this.movementSystem.setControl("forward", true);
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankMoveForward", {
        playerId: this.playerId,
      });
    }
  }

  startMovingBackwardNetwork(): void {
    if (this.isDestroyed) return;
    this.controls.backward = true;
    this.controls.forward = false;
    this.movementSystem.setControl("backward", true);
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankMoveBackward", {
        playerId: this.playerId,
      });
    }
  }

  startTurningLeftNetwork(): void {
    if (this.isDestroyed) return;
    this.controls.left = true;
    this.controls.right = false;
    this.movementSystem.setControl("left", true);
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankTurnLeft", {
        playerId: this.playerId,
      });
    }
  }

  startTurningRightNetwork(): void {
    if (this.isDestroyed) return;
    this.controls.right = true;
    this.controls.left = false;
    this.movementSystem.setControl("right", true);
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankTurnRight", {
        playerId: this.playerId,
      });
    }
  }

  stopMovingNetwork(): void {
    if (this.isDestroyed) return;

    // Reset all movement controls
    this.controls.forward = false;
    this.controls.backward = false;
    this.controls.left = false;
    this.controls.right = false;

    // Reset movement system controls
    this.movementSystem.setControl("forward", false);
    this.movementSystem.setControl("backward", false);
    this.movementSystem.setControl("left", false);
    this.movementSystem.setControl("right", false);

    // Reset speeds
    this.currentSpeed = 0;
    this.currentTurnSpeed = 0;

    // Send stop event to server if this is local tank
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankStop", {
        playerId: this.playerId,
        position: {
          x: this.mesh.position.x,
          y: this.mesh.position.y,
          z: this.mesh.position.z,
        },
        rotation: this.mesh.rotation.y,
      });
    }
  }

  // Methods for network control of turret rotation
  startTurretTurningLeft(): void {
    if (this.isDestroyed || !this.turret) return;

    this.turretTurnDirection = 1;
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankTurretTurnLeft", {
        playerId: this.playerId,
        turretRotation: this.turret.rotation.y,
      });
    }
  }

  startTurretTurningRight(): void {
    if (this.isDestroyed || !this.turret) return;

    this.turretTurnDirection = -1;
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankTurretTurnRight", {
        playerId: this.playerId,
        turretRotation: this.turret.rotation.y,
      });
    }
  }

  stopTurretTurning(): void {
    if (this.isDestroyed || !this.turret) return;

    this.turretTurnDirection = 0;
    if (window.playerTank === this && window.socket) {
      window.socket.emit("tankTurretStop", {
        playerId: this.playerId,
        turretRotation: this.turret.rotation.y,
      });
    }
  }

  /**
   * Starts handbrake
   */
  startHandbrake(): void {
    if (this.isDestroyed) return;
    this.controls.handbrake = true;
    // Pass information to movement controller
    this.movementSystem.setHandbrakeState(true);
  }

  /**
   * Stops handbrake
   */
  stopHandbrake(): void {
    if (this.isDestroyed) return;
    this.controls.handbrake = false;
    // Pass information to movement controller
    this.movementSystem.setHandbrakeState(false);

    // Restore standard movement parameters
    this.acceleration = TANK_CONFIG.MOVEMENT.ACCELERATION;
    this.deceleration = TANK_CONFIG.MOVEMENT.DECELERATION;
  }

  /**
   * Converts tank speed from game units to km/h
   * @returns Speed in km/h
   */
  getSpeedKmh(): number {
    return this.movementSystem.getDisplaySpeed();
  }

  /**
   * Gets the current speed in game units
   */
  getCurrentSpeed(deltaTime: number): number {
    return this.movementSystem.getSpeed(deltaTime);
  }

  /**
   * Gets the boost status
   */
  getBoostStatus(): boolean {
    return this.isBoosting;
  }

  /**
   * Sets references to environment elements
   * @param environment Object containing environment elements
   */
  setEnvironment(environment: Environment) {
    this.environment = environment;
    // Pass environment to movement controller
    this.movementSystem.setEnvironment(environment);
  }

  public isInRespawnZone(): boolean {
    const position = this.mesh.position;
    const halfSize = RESPAWN_ZONE_CONFIG.SIZE / 2;
    const inZone = Math.abs(position.x) <= halfSize && Math.abs(position.z) <= halfSize;

    // Debug log for respawn zone
    if (inZone) {
      //some info
    }

    return inZone;
  }

  public isInSafeTime(): boolean {
    return this.spawnController.isInSafeTime();
  }

  public checkTankCollisions(): void {
    // Use central function from environment.ts
    if (!this.mesh.parent) return;

    const tanks = this.mesh.parent.children
      .filter((child) => child.userData.type === "tank")
      .map((tankMesh) => tankMesh.userData.tank as Tank)
      .filter((tank) => tank); // Remove undefined/null

    checkTankCollisions(this, tanks);
  }

  // Obsługa zdarzeń klawiatury
  handleKeyDown(event: KeyboardEvent) {
    if (event.repeat) return;

    switch (event.code) {
      case "ArrowUp":
        this.controls.barrelUp = true;
        this.barrelController.setControls({
          barrelUp: true,
          barrelDown: false,
        });
        break;
      case "ArrowDown":
        this.controls.barrelDown = true;
        this.barrelController.setControls({
          barrelUp: false,
          barrelDown: true,
        });
        break;
    }
  }

  handleKeyUp(event: KeyboardEvent) {
    switch (event.code) {
      case "ArrowUp":
        this.controls.barrelUp = false;
        this.barrelController.setControls({
          barrelUp: false,
          barrelDown: this.controls.barrelDown,
        });
        break;
      case "ArrowDown":
        this.controls.barrelDown = false;
        this.barrelController.setControls({
          barrelUp: this.controls.barrelUp,
          barrelDown: false,
        });
        break;
    }
  }

  // Mouse movement handling
  handleMouseMove(event: MouseEvent) {
    // Turret rotation
    const mouseSensitivity = MOUSE_CONFIG.SENSITIVITY;
    this.targetTurretAngle -= event.movementX * mouseSensitivity;
  }

  // Method to set initialization state
  setInitialized(value: boolean) {
    this.initialized = value;

    // Initialize mobile controller if this is the player's tank
    if (value && window.playerTank === this) {
      this.initMobileController();
    }
  }

  // Public method to check if tank is initialized
  public isInitialized(): boolean {
    return this.initialized;
  }

  // Initialize mobile controller
  initMobileController() {
    // Create mobile controller if it doesn't exist
    if (!this.mobileController) {
      this.mobileController = new MobileController(this);

      // Log whether we're actually on mobile
      console.log("[Tank] Mobile controller created, isMobile:", this.mobileController.getIsMobile());
    }

    // Only attempt to initialize if we're actually on a mobile device
    if (this.mobileController.getIsMobile()) {
      console.log("[Tank] Initializing mobile controls");
      this.mobileController.initialize();
    } else {
      console.log("[Tank] Not initializing mobile controls on non-mobile device");
    }
  }

  // Method to handle tank destruction
  destroy() {
    this.isDestroyed = true;
    this.spawnController.destroy();
  }

  // Method to handle tank respawn
  spawn() {
    this.spawnController.spawn();
  }

  public dispose(): void {
    const disposedGeometries = new Set<THREE.BufferGeometry>();
    const disposedMaterials = new Set<THREE.Material>();

    // Clean up Three.js objects
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Dispose of geometry
          if (child.geometry) {
            if (!child.geometry.userData.isShared && !disposedGeometries.has(child.geometry)) {
              disposedGeometries.add(child.geometry);
              child.geometry.dispose();
            }
          }
          // Dispose of materials and textures
          if (child.material) {
            const meshMaterial = child.material;

            if (Array.isArray(meshMaterial)) {
              meshMaterial.forEach((material) => {
                if (material.userData.isShared) return;
                if (disposedMaterials.has(material)) return;
                disposedMaterials.add(material);
                if ((material as any).map) (material as any).map.dispose();
                material.dispose();
              });
            } else {
              const material = meshMaterial;
              if (!material.userData.isShared && !disposedMaterials.has(material)) {
                disposedMaterials.add(material);
                if ((material as any).map) (material as any).map.dispose();
                material.dispose();
              }
            }
          }
        }
      });

      // Remove from scene
      if (this.mesh.parent) {
        this.mesh.parent.remove(this.mesh);
      }
    }

    // Destroy mobile controller if it exists
    if (this.mobileController) {
      this.mobileController.destroy();
      this.mobileController = null;
    }

    // Clean up tank-specific features
    if (this.spawning) {
      this.spawning.cleanupDestroyedEffects();
    }

    // Clean up event listeners
    EventManager.removeAllListeners(this.playerId);

    // Clear references
    this.environment = null;
    this.controls = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      handbrake: false,
      barrelUp: false,
      barrelDown: false,
    };

    // Remove from global state if exists
    if (window.gameState?.tanks) {
      const index = window.gameState.tanks.indexOf(this);
      if (index > -1) {
        window.gameState.tanks.splice(index, 1);
      }
    }

    // Remove from remote tanks array if exists
    if (window.remoteTanks) {
      const index = window.remoteTanks.indexOf(this);
      if (index > -1) {
        window.remoteTanks.splice(index, 1);
      }
    }

    console.log(`[Tank] Disposed tank ${this.playerId}`);
  }

  setUsername(username: string) {
    this.username = username || "Anonymous";
    this.updateUsernameLabel();
  }

  private createUsernameLabel() {
    // Create a canvas for the username text
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) return;

    canvas.width = 256;
    canvas.height = 64;

    // Create a texture from the canvas
    const texture = new THREE.CanvasTexture(canvas);

    // Create sprite material using the texture
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    // Create the sprite and position it above the tank
    this.usernameLabel = new THREE.Sprite(material);
    this.usernameLabel.position.set(0, 0, 0); // Position above the tank
    this.usernameLabel.scale.set(2.5, 1, 1);

    // Add to mesh
    this.mesh.add(this.usernameLabel);

    // Update the label with the current username
    this.updateUsernameLabel();
  }

  private updateUsernameLabel() {
    if (!this.usernameLabel) return;

    // Get the canvas from the sprite's texture
    const texture = this.usernameLabel.material.map as THREE.CanvasTexture;
    if (!texture) return;

    const canvas = texture.image as HTMLCanvasElement;
    const context = canvas.getContext("2d");
    if (!context) return;

    // Clear the canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    // Set text style
    context.font = "24px Arial";
    context.fillStyle = "white";
    context.textAlign = "center";
    context.textBaseline = "middle";

    // Add a background for better visibility
    /*
    context.fillStyle = "rgba(0, 0, 0, 0.5)";
    const textWidth = context.measureText(this.username).width;
    context.fillRect(
      (canvas.width - textWidth) / 2 - 10,
      (canvas.height - 24) / 2 - 2,
      textWidth + 20,
      28
    );
    */

    // Draw the text
    context.fillStyle = "white";
    context.fillText(this.username, canvas.width / 2, canvas.height / 2);

    // Update the texture
    texture.needsUpdate = true;
  }
}
